/**
 * TransitionManager — unified per-frame transition tick for the scene.
 *
 * Both property transitions (Properties behavior) and transform transitions
 * (SceneObject) delegate to this shared service. One `onBeforeRenderObservable`
 * for all active transitions in the scene.
 *
 * Each transition is identified by a unique key (caller-defined, e.g.
 * "props:42:color" or "transform:pos:42"). The manager handles:
 * - Start time + delay
 * - Easing
 * - Per-frame progress computation
 * - Completion detection
 * - Interruption (start replaces existing transition at the same key)
 *
 * The manager does NOT interpolate values — callers provide `onUpdate(progress)`
 * and `onComplete()` callbacks and perform their own type-specific interpolation.
 * This keeps the manager generic: it only computes eased progress [0..1].
 */

import type { Scene } from '@babylonjs/core/scene';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { TimeTransition } from '../scenedata/behaviors/propertiesdata';
import { getEasingFunction } from './canopy/TransitionUtils';

/** WeakMap so managers are garbage-collected when the scene is disposed. */
const _managers = new WeakMap<Scene, TransitionManager>();

/**
 * Get the shared TransitionManager for a Babylon Scene.
 * Creates one lazily on first access. One manager per scene.
 */
export function getTransitionManager(scene: Scene): TransitionManager {
    let tm = _managers.get(scene);
    if (!tm) {
        tm = new TransitionManager(scene);
        _managers.set(scene, tm);
    }
    return tm;
}

/**
 * Active transition entry.
 */
interface ActiveTransition {
    config: TimeTransition;
    startTime: number;
    /** Called each frame with eased progress [0..1]. Called with exactly 1.0 on completion. */
    onUpdate: (progress: number) => void;
    /** Called once when the transition completes (after onUpdate(1.0)). */
    onComplete?: () => void;
}

export class TransitionManager {
    private _scene: Scene;
    private _transitions: Map<string, ActiveTransition> = new Map();
    private _observer: Observer<Scene> | null = null;
    private _now: () => number = () => performance.now();
    private _after: (ms: number, fn: () => void) => () => void = (ms, fn) => {
        const timer = setTimeout(fn, ms);
        return () => clearTimeout(timer);
    };
    /** `after` timers on an injected clock, completed by `_tick`. Not transitions: `cancelAll` leaves them, as it leaves a `setTimeout`. */
    private _timers = new Map<number, { due: number; fn: () => void }>();
    private _timerCount = 0;

    constructor(scene: Scene) {
        this._scene = scene;
    }

    /**
     * Start (or restart) a transition.
     *
     * If a transition with the same key is already active, it's replaced —
     * the caller is responsible for capturing the current interpolated state
     * as the new "from" value before calling start (interruption pattern).
     *
     * @param key - Unique identifier for this transition (e.g. "props:42:color", "transform:pos:42")
     * @param config - Time-driven config (duration, easing, delay)
     * @param onUpdate - Called each frame with eased progress [0..1]. Also called with 1.0 on completion.
     * @param onComplete - Called once after the final onUpdate(1.0).
     */
    start(
        key: string,
        config: TimeTransition,
        onUpdate: (progress: number) => void,
        onComplete?: () => void,
    ): void {
        this._transitions.set(key, {
            config,
            startTime: this._now() + (config.delay ?? 0) * 1000,
            onUpdate,
            onComplete,
        });
        this._ensureObserver();

        // Fire initial update at progress 0 so callers see the "from" state immediately
        onUpdate(0);
    }

    /**
     * Cancel a transition by key.
     * Does NOT call onComplete or onUpdate.
     */
    cancel(key: string): void {
        this._transitions.delete(key);
        this._removeObserverIfIdle();
    }

    /**
     * Cancel all transitions matching a prefix.
     * Useful for cleanup: `cancelByPrefix("props:42:")` cancels all property transitions on that object.
     */
    cancelByPrefix(prefix: string): void {
        for (const key of this._transitions.keys()) {
            if (key.startsWith(prefix)) {
                this._transitions.delete(key);
            }
        }
        this._removeObserverIfIdle();
    }

    /**
     * Cancel all transitions. Pending `after` timers are not transitions and are kept, on either clock; their owners cancel them.
     */
    cancelAll(): void {
        this._transitions.clear();
        this._removeObserverIfIdle();
    }

    /** Whether a specific transition is active. */
    has(key: string): boolean {
        return this._transitions.has(key);
    }

    /** Number of active transitions (for testing). */
    get activeCount(): number {
        return this._transitions.size;
    }

    /**
     * Inject a clock (ms) for hosts that step a scene on their own time, such as a headless review. Default: `performance.now`.
     * Transitions read it from then on, and `after` timers armed afterwards run on it, completed by this manager's ticks.
     * Timers armed before the call keep the clock they were armed on, so call it once, before the scene plays: a review sets
     * it on a disposable copy before playing it.
     */
    setClock(now: () => number): void {
        this._now = now;
        this._after = (ms, fn) => {
            const id = this._timerCount++;
            this._timers.set(id, { due: now() + ms, fn });
            this._ensureObserver();
            return () => { this._timers.delete(id); };
        };
    }

    /**
     * Call `fn` once `ms` of this scene's time has passed: wall time (`setTimeout`) by default, the injected clock after `setClock`.
     * Returns a cancel. Behaviors schedule their delays through this so a stepped scene sees them on its clock.
     */
    after(ms: number, fn: () => void): () => void {
        return this._after(ms, fn);
    }

    /**
     * Dispose the manager: cancel all transitions and pending injected-clock timers, remove the observer. Pending wall-time
     * `after` timers are `setTimeout`s their owners cancel on detach, as before `after` existed.
     */
    dispose(): void {
        this._timers.clear();
        this.cancelAll();
    }

    // ── Internals ──

    private _tick(): void {
        this._tickTransitions();
        // After the transitions, so a timer due at a transition's end runs after its last frame.
        if (this._timers.size > 0) this._fireDueTimers();
        this._removeObserverIfIdle();
    }

    private _tickTransitions(): void {
        if (this._transitions.size === 0) return;

        const now = this._now();
        const completed: Array<[string, ActiveTransition]> = [];

        for (const [key, t] of this._transitions) {
            const elapsed = now - t.startTime;

            // Still in delay period
            if (elapsed < 0) continue;

            const durationMs = t.config.duration * 1000;
            const rawProgress = durationMs > 0 ? Math.min(elapsed / durationMs, 1) : 1;

            if (rawProgress >= 1) {
                t.onUpdate(1);
                completed.push([key, t]);
            } else {
                const easingFn = getEasingFunction(t.config.easing);
                t.onUpdate(easingFn(rawProgress));
            }
        }

        // An earlier onComplete may have replaced or cancelled a later entry.
        // Its completion is then dropped, as for any replace or cancel.
        for (const [key, t] of completed) {
            if (this._transitions.get(key) !== t) continue;
            this._transitions.delete(key);
            t.onComplete?.();
        }
    }

    private _fireDueTimers(): void {
        const now = this._now();
        const due = [...this._timers].filter(([, t]) => t.due <= now).sort((a, b) => a[1].due - b[1].due || a[0] - b[0]);
        for (const [id, t] of due) {
            // An earlier timer may have cancelled this one.
            if (!this._timers.delete(id)) continue;
            t.fn();
        }
    }

    private _removeObserverIfIdle(): void {
        if (this._transitions.size === 0 && this._timers.size === 0) this._removeObserver();
    }

    private _ensureObserver(): void {
        if (this._observer) return;
        this._observer = this._scene.onBeforeRenderObservable.add(() => {
            this._tick();
        });
    }

    private _removeObserver(): void {
        if (!this._observer) return;
        this._scene.onBeforeRenderObservable.remove(this._observer);
        this._observer = null;
    }
}
